import { ExecutionRunBreakdownCell } from '@/features/executionRuns/components/ExecutionRunBreakdownCell';
import { resolveExecutionRunRunSummary } from '@/features/executionRuns/components/executionRunRunSummary';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  CLONE_META_DETAILS_LINK_SX as EXECUTION_DETAILS_LINK_SX,
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { buildExecutionRunDetailPath } from '@/features/shared/components/tableSystem/tableRoutes';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { Box, formatDuration } from '@/ui';
import { getStatusIconComponent, TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { resolveStatusChipTone } from '@/ui/primitives';
import {
  EventOutlinedIcon,
  PersonOutlineOutlinedIcon,
  TimerOutlinedIcon,
  UploadFileIcon,
} from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { ExecutionRunDto } from '@flowqa/shared';

const columnWidths = tableColumnWidths.executionRuns;

export const buildExecutionRunsColumns = ({
  groupErroredWithFailed = false,
  editRowPendingId = null,
  deleteRowPendingId = null,
  organisationId = null,
  onEditRow,
  onDeleteRow,
  tagColorByName = {},
}: {
  groupErroredWithFailed?: boolean;
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
  organisationId?: string | null;
  onEditRow?: (row: ExecutionRunDto) => void;
  onDeleteRow?: (row: ExecutionRunDto) => void;
  tagColorByName?: Partial<Record<string, TagColor>>;
} = {}): Array<GridColDef<ExecutionRunDto>> => [
  {
    field: 'name',
    headerName: getDetailLabel('run'),
    flex: columnWidths.name.flex,
    sortable: false,
    cellClassName: 'table-name-cell',
    valueGetter: (_value, row) => row.name,
    renderCell: (params) => {
      const runSummary = resolveExecutionRunRunSummary(params.row, tagColorByName);
      return (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label: runSummary.primary.label,
              href: runSummary.primary.href,
            }}
            secondary={
              runSummary.secondary
                ? {
                    label: runSummary.secondary.label,
                    role: 'caption',
                    href: runSummary.secondary.href ?? '/results-files',
                  }
                : null
            }
            tags={runSummary.tags}
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'status',
    headerName: getDetailLabel('status'),
    flex: columnWidths.status.flex,
    align: 'left',
    headerAlign: 'left',
    sortable: false,
    filterable: false,
    cellClassName: 'table-status-meta-cell',
    renderCell: (params) => {
      const displayStatus = resolveProjectDisplayStatus(params.row.status, groupErroredWithFailed);
      const statusTone = resolveStatusChipTone(displayStatus);
      const timestamp = params.row.startedAt ?? params.row.finishedAt;
      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={resolveProjectDisplayStatusLabel(
              params.row.status,
              groupErroredWithFailed,
            )}
            statusHref={buildExecutionRunDetailPath(params.row.id)}
            onStatusClick={(event) => {
              event.stopPropagation();
            }}
            chipTone={statusTone}
            chipIcon={<Box component={getStatusIconComponent(displayStatus)} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={formatDuration(params.row.durationMs ?? null) || ''}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={formatRelativeTime(timestamp) || '-'}
            timestampTitle={formatDate(timestamp, { includeTime: true }) || ''}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'suites',
    headerName: getDetailLabel('suites'),
    flex: columnWidths.suites.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => (
      <TableCellContainer gap="none">
        <ExecutionRunBreakdownCell kind="suite" organisationId={organisationId} row={params.row} />
      </TableCellContainer>
    ),
  },
  {
    field: 'tests',
    headerName: getDetailLabel('tests'),
    flex: columnWidths.tests.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => (
      <TableCellContainer gap="none">
        <ExecutionRunBreakdownCell kind="test" organisationId={organisationId} row={params.row} />
      </TableCellContainer>
    ),
  },
  {
    field: 'details',
    headerName: getDetailLabel('details'),
    flex: columnWidths.details.flex,
    align: 'left',
    headerAlign: 'left',
    sortable: false,
    filterable: false,
    cellClassName: 'table-meta-details-cell',
    renderCell: (params) => {
      const uploadId = params.row.uploadIds.length > 0 ? params.row.uploadIds[0] : null;
      const uploadedByLabel = params.row.createdByEmail ?? params.row.createdByName ?? '-';
      const uploadedAt = params.row.resultsFileCreatedAt;
      const uploadedLabel = formatRelativeTime(uploadedAt);
      const uploadedTitle = formatDate(uploadedAt, { includeTime: true });

      return (
        <TableCellContainer gap="none">
          <TableBreakdownStack>
            <TableBreakdownLine
              rowTitle="Upload"
              icon={<UploadFileIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
              content={uploadId ?? '-'}
              href={uploadId ? `/uploads/${uploadId}` : null}
              onClick={(event) => {
                event.stopPropagation();
              }}
              linkSx={EXECUTION_DETAILS_LINK_SX}
            />
            <TableBreakdownLine
              rowTitle="Uploaded By"
              icon={
                <PersonOutlineOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
              }
              content={uploadedByLabel}
            />
            <TableBreakdownLine
              rowTitle="Uploaded"
              icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
              content={uploadedLabel && uploadedLabel.length > 0 ? uploadedLabel : '-'}
              valueTitle={uploadedTitle}
              contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
              valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
            />
          </TableBreakdownStack>
        </TableCellContainer>
      );
    },
  },
  {
    field: 'actions',
    headerName: '',
    flex: columnWidths.actions.flex,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams<ExecutionRunDto>) => (
      <TableActionsCell
        rowName={params.row.name?.trim() ?? params.row.id}
        editActionLabel={getEntityActionLabel('edit', 'Execution Run')}
        deleteActionLabel={getEntityActionLabel('delete', 'Execution Run')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableEdit={editRowPendingId === params.row.id || deleteRowPendingId === params.row.id}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  },
];
